"use client";

import { useEffect, useState } from "react";
import Image from "next/image";

import type {
  DetectionBox,
  DemoFinetune,
  DemoImageAsset,
  HeroDetectionOverlay,
  HeroPointOverlay,
  ImageOverlayPairExample,
  SingleImageAnswerPairExample,
} from "@/lib/demo-finetunes";
import { cn } from "@/lib/utils";
import PageContent from "@/components/page-content";
import { Card, CardContent } from "@/components/ui/card";
import { DetectionOverlay } from "./DetectionOverlay";
import { PointOverlay } from "./PointOverlay";
import { SectionHeading } from "./SectionHeading";

interface ResultsSectionProps {
  finetune: DemoFinetune;
}

function getImageOverlayExamples(
  finetune: DemoFinetune
): ImageOverlayPairExample[] | null {
  return finetune.comparisonMode === "image_overlay_pair"
    ? finetune.comparisonExamples
    : null;
}

function getSingleImageAnswerExamples(
  finetune: DemoFinetune
): SingleImageAnswerPairExample[] | null {
  return finetune.comparisonMode === "single_image_answer_pair"
    ? finetune.comparisonExamples
    : null;
}

function getExampleLabel(title: string | undefined, index: number) {
  const trimmedTitle = title?.trim();

  return trimmedTitle || `Example ${index + 1}`;
}

function formatDelta(delta: number | undefined) {
  if (typeof delta !== "number" || delta <= 0) {
    return null;
  }

  return `+${delta.toFixed(3)}`;
}

function getSharedPrompt(examples: SingleImageAnswerPairExample[]) {
  const firstPrompt = examples[0]?.prompt.trim();

  if (!firstPrompt) {
    return null;
  }

  return examples.every((example) => example.prompt.trim() === firstPrompt)
    ? firstPrompt
    : null;
}

function PromptCard({ prompt }: { prompt: string }) {
  return (
    <Card className="border-border/60 bg-card/80 shadow-sm">
      <CardContent className="p-5 sm:p-6">
        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
          Prompt
        </p>
        <p className="mt-2 text-sm leading-7 text-foreground sm:text-base">
          {prompt}
        </p>
      </CardContent>
    </Card>
  );
}

function ExampleTabs({
  labels,
  activeIndex,
  onSelect,
}: {
  labels: string[];
  activeIndex: number;
  onSelect: (index: number) => void;
}) {
  if (labels.length <= 1) {
    return null;
  }

  return (
    <div className="overflow-x-auto pb-1">
      <div
        className="flex w-max items-center gap-2 rounded-full border border-border/60 bg-muted/20 px-3 py-2"
        role="tablist"
        aria-label="Result examples"
      >
        {labels.map((label, index) => {
          const isActive = index === activeIndex;

          return (
            <button
              key={`${label}-${index}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={`Show ${label}`}
              className={cn(
                "flex h-7 items-center justify-center rounded-full px-1 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                isActive ? "px-2.5" : "hover:scale-110"
              )}
              onClick={() => onSelect(index)}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "block rounded-full transition-all duration-200",
                  isActive
                    ? "h-2.5 w-7 bg-foreground"
                    : "h-2.5 w-2.5 bg-muted-foreground/40 hover:bg-muted-foreground/65"
                )}
              />
              <span className="sr-only">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OverlayLegend({ kind }: { kind: "detect" | "point" }) {
  return (
    <div className="flex flex-wrap items-center gap-5 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span
          className={cn(
            "inline-block bg-green-500",
            kind === "point" ? "h-2 w-2 rounded-full" : "h-0.5 w-4 rounded"
          )}
        />
        Correct
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className={cn(
            "inline-block bg-red-500",
            kind === "point" ? "h-2 w-2 rounded-full" : "h-0.5 w-4 rounded"
          )}
        />
        False positive
      </span>
    </div>
  );
}

function getBoxIoU(a: DetectionBox, b: DetectionBox) {
  const xMin = Math.max(a.xMin, b.xMin);
  const yMin = Math.max(a.yMin, b.yMin);
  const xMax = Math.min(a.xMax, b.xMax);
  const yMax = Math.min(a.yMax, b.yMax);
  const intersectionWidth = Math.max(0, xMax - xMin);
  const intersectionHeight = Math.max(0, yMax - yMin);
  const intersectionArea = intersectionWidth * intersectionHeight;

  if (intersectionArea <= 0) {
    return 0;
  }

  const areaA = Math.max(0, a.xMax - a.xMin) * Math.max(0, a.yMax - a.yMin);
  const areaB = Math.max(0, b.xMax - b.xMin) * Math.max(0, b.yMax - b.yMin);
  const unionArea = areaA + areaB - intersectionArea;

  return unionArea > 0 ? intersectionArea / unionArea : 0;
}

function getBestDetectionMatchSum(
  gt: DetectionBox[],
  predictions: DetectionBox[],
  gtIndex = 0,
  usedPredictionMask = 0n,
  memo = new Map<string, number>()
): number {
  if (gtIndex >= gt.length) {
    return 0;
  }

  const memoKey = `${gtIndex}:${usedPredictionMask}`;
  const cached = memo.get(memoKey);

  if (cached != null) {
    return cached;
  }

  let best = getBestDetectionMatchSum(
    gt,
    predictions,
    gtIndex + 1,
    usedPredictionMask,
    memo
  );

  for (let predictionIndex = 0; predictionIndex < predictions.length; predictionIndex += 1) {
    const predictionBit = 1n << BigInt(predictionIndex);

    if (usedPredictionMask & predictionBit) {
      continue;
    }

    const iou = getBoxIoU(gt[gtIndex]!, predictions[predictionIndex]!);
    const candidate =
      iou +
      getBestDetectionMatchSum(
        gt,
        predictions,
        gtIndex + 1,
        usedPredictionMask | predictionBit,
        memo
      );

    if (candidate > best) {
      best = candidate;
    }
  }

  memo.set(memoKey, best);

  return best;
}

function getDetectionMeanIoU(overlay: HeroDetectionOverlay) {
  const gt = overlay.gt;
  const predictions = overlay.predictions;

  if (!gt.length && !predictions.length) {
    return 1;
  }

  if (!gt.length || !predictions.length) {
    return 0;
  }

  const matchedIoUSum = getBestDetectionMatchSum(gt, predictions);

  return matchedIoUSum / Math.max(gt.length, predictions.length);
}

function ResultExampleControls({
  labels,
  activeIndex,
  onSelect,
  legendKind,
}: {
  labels: string[];
  activeIndex: number;
  onSelect: (index: number) => void;
  legendKind?: "detect" | "point";
}) {
  if (labels.length <= 1 && !legendKind) {
    return null;
  }

  const hasLegend = Boolean(legendKind);

  return (
    <div
      className={cn(
        "w-full gap-4",
        hasLegend
          ? "flex flex-wrap items-center justify-between"
          : "flex justify-center"
      )}
    >
      <ExampleTabs
        labels={labels}
        activeIndex={activeIndex}
        onSelect={onSelect}
      />
      {legendKind ? (
        <div className="shrink-0">
          <OverlayLegend kind={legendKind} />
        </div>
      ) : null}
    </div>
  );
}

function ResultImagePanel({
  image,
  label,
  overlay,
  pointOverlay,
  metricLabel,
  metricValue,
  delta,
}: {
  image: DemoImageAsset;
  label: string;
  overlay?: HeroDetectionOverlay;
  pointOverlay?: HeroPointOverlay;
  metricLabel?: string;
  metricValue?: number;
  delta?: number;
}) {
  const formattedDelta = formatDelta(delta);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
          {label}
        </p>
        {metricLabel && typeof metricValue === "number" ? (
          <span className="rounded-md bg-muted/40 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            {metricLabel} {metricValue.toFixed(3)}
          </span>
        ) : null}
        {formattedDelta ? (
          <span
            className="rounded-md bg-emerald-500/10 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300"
          >
            {formattedDelta}
          </span>
        ) : null}
      </div>

      <div
        className="relative overflow-hidden rounded-2xl border border-border/60 bg-background/70"
        style={{ aspectRatio: `${image.width} / ${image.height}` }}
      >
        <Image
          src={image.src}
          alt={image.alt}
          fill
          sizes="(min-width: 1024px) 45vw, 100vw"
          className="object-contain"
        />
        {overlay ? <DetectionOverlay overlay={overlay} /> : null}
        {pointOverlay ? <PointOverlay overlay={pointOverlay} /> : null}
      </div>
    </div>
  );
}

function PredictionCard({
  label,
  answer,
  note,
  tone,
}: {
  label: string;
  answer: string;
  note?: string;
  tone: "base" | "finetune";
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border p-5",
        tone === "base"
          ? "border-red-500/20 bg-red-500/5"
          : "border-emerald-500/20 bg-emerald-500/5"
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        {note ? (
          <span
            className={cn(
              "rounded-full px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em]",
              tone === "base"
                ? "bg-red-500/10 text-red-700 dark:text-red-300"
                : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
            )}
          >
            {note}
          </span>
        ) : null}
      </div>
      <p className="mt-3 text-sm leading-7 text-muted-foreground">{answer}</p>
    </div>
  );
}

function AnswerExampleCard({
  example,
  showPrompt,
}: {
  example: SingleImageAnswerPairExample;
  showPrompt: boolean;
}) {
  const title = example.title?.trim();

  return (
    <Card className="overflow-hidden border-border/60 bg-card/80 shadow-sm">
      <CardContent className="space-y-5 p-4 sm:p-5">
        {title ? (
          <div>
            <h3 className="text-xl font-semibold tracking-tight text-foreground">
              {title}
            </h3>
          </div>
        ) : null}

        <div
          className="relative overflow-hidden rounded-2xl border border-border/60 bg-background/70"
          style={{
            aspectRatio: `${example.image.width} / ${example.image.height}`,
          }}
        >
          <Image
            src={example.image.src}
            alt={example.image.alt}
            fill
            sizes="(min-width: 1280px) 30vw, (min-width: 768px) 45vw, 100vw"
            className="object-contain"
          />
        </div>

        <div className="space-y-3">
          <PredictionCard
            label={example.before.label}
            answer={example.before.answer}
            note={example.before.note}
            tone="base"
          />
          <PredictionCard
            label={example.after.label}
            answer={example.after.answer}
            note={example.after.note}
            tone="finetune"
          />
        </div>

        {showPrompt ? (
          <div className="rounded-2xl border border-border/60 bg-background/60 p-4">
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Prompt
            </p>
            <p className="mt-2 text-sm leading-6 text-foreground">
              {example.prompt}
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function GalleryFallback({ finetune }: { finetune: DemoFinetune }) {
  if (!finetune.sampleImages.length) {
    return null;
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
      {finetune.sampleImages.map((image) => (
        <Card
          key={`${finetune.slug}-${image.src}-${image.caption}`}
          className="overflow-hidden border-border/60 bg-card/80 shadow-sm"
        >
          <CardContent className="p-0">
            <div className="relative aspect-[16/10] bg-background/70">
              <Image
                src={image.src}
                alt={image.alt}
                fill
                sizes="(min-width: 1280px) 30vw, (min-width: 768px) 45vw, 100vw"
                className="object-contain"
              />
            </div>

            <div className="p-5">
              <p className="text-sm leading-7 text-muted-foreground">
                {image.caption}
              </p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ResultsSection({ finetune }: ResultsSectionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoAdvancePaused, setIsAutoAdvancePaused] = useState(false);
  const imageOverlayExamples = getImageOverlayExamples(finetune) ?? [];
  const answerExamples = getSingleImageAnswerExamples(finetune) ?? [];
  const detectionShowcase = finetune.detectionShowcase ?? [];
  const pointShowcase = finetune.pointShowcase ?? [];
  const sharedAnswerPrompt = getSharedPrompt(answerExamples);

  const variant =
    detectionShowcase.length
      ? "detection"
      : pointShowcase.length
        ? "point"
        : answerExamples.length
          ? "answer"
          : imageOverlayExamples.length
            ? "image-overlay"
            : finetune.sampleImages.length
              ? "gallery"
              : null;

  const tabLabels =
    variant === "detection"
      ? detectionShowcase.map((example, index) =>
          getExampleLabel(example.title, index)
        )
      : variant === "point"
        ? pointShowcase.map((_, index) =>
            getExampleLabel(answerExamples[index]?.title, index)
          )
        : variant === "answer"
          ? answerExamples.map((example, index) =>
              getExampleLabel(example.title, index)
            )
          : variant === "image-overlay"
            ? imageOverlayExamples.map((example, index) =>
                getExampleLabel(example.title, index)
              )
            : [];

  useEffect(() => {
    setActiveIndex(0);
    setIsAutoAdvancePaused(false);
  }, [finetune.slug]);

  useEffect(() => {
    if (tabLabels.length <= 1 || isAutoAdvancePaused) {
      return;
    }

    const timer = window.setTimeout(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % tabLabels.length);
    }, 10_000);

    return () => window.clearTimeout(timer);
  }, [activeIndex, isAutoAdvancePaused, tabLabels.length]);

  if (!variant) {
    return null;
  }

  const safeIndex = tabLabels.length
    ? Math.min(activeIndex, tabLabels.length - 1)
    : 0;
  const autoAdvanceHandlers =
    tabLabels.length > 1
      ? {
          onMouseEnter: () => setIsAutoAdvancePaused(true),
          onMouseLeave: () => setIsAutoAdvancePaused(false),
        }
      : undefined;
  const sectionDescription =
    variant === "answer"
      ? "Compare the base model against the fine-tuned model across representative benchmark examples."
      : variant === "gallery"
        ? "Representative benchmark examples from the fine-tune."
        : "Switch between benchmark examples to compare the base model against the fine-tuned model on the same task.";

  return (
    <PageContent>
      <section className="space-y-8">
        <SectionHeading
          eyebrow="Results"
          title="See it in action"
          description={sectionDescription}
          fullWidthDescription
        />

        {variant === "gallery" ? (
          <GalleryFallback finetune={finetune} />
        ) : (
          <>
            {variant === "detection" ? (
              (() => {
                const activeExample = detectionShowcase[safeIndex]!;
                const beforeMiou = getDetectionMeanIoU(activeExample.before);
                const afterMiou = getDetectionMeanIoU(activeExample.after);
                const delta = afterMiou - beforeMiou;

                return (
                  <>
                    <div className="space-y-6">
                      <PromptCard prompt={finetune.codePrompt} />

                      <div className="space-y-4">
                        <Card
                          className="overflow-hidden border-border/60 bg-card/80 shadow-sm"
                          {...autoAdvanceHandlers}
                        >
                          <CardContent className="p-4 sm:p-6">
                            <div className="grid gap-6 lg:grid-cols-2">
                              <ResultImagePanel
                                image={activeExample.image}
                                label="Base Moondream 3 Preview"
                                overlay={activeExample.before}
                                metricLabel="mIoU"
                                metricValue={beforeMiou}
                              />
                              <ResultImagePanel
                                image={activeExample.image}
                                label="Fine-tuned Moondream 3 Preview"
                                overlay={activeExample.after}
                                metricLabel="mIoU"
                                metricValue={afterMiou}
                                delta={delta}
                              />
                            </div>
                          </CardContent>
                        </Card>

                        <ResultExampleControls
                          labels={tabLabels}
                          activeIndex={safeIndex}
                          onSelect={setActiveIndex}
                          legendKind="detect"
                        />
                      </div>
                    </div>
                  </>
                );
              })()
            ) : variant === "point" ? (
              (() => {
                const activeExample = pointShowcase[safeIndex]!;
                const relatedComparison = answerExamples[safeIndex];

                return (
                  <>
                    <div className="space-y-6">
                      <PromptCard
                        prompt={relatedComparison?.prompt ?? finetune.codePrompt}
                      />

                      <div className="space-y-4">
                        <Card
                          className="overflow-hidden border-border/60 bg-card/80 shadow-sm"
                          {...autoAdvanceHandlers}
                        >
                          <CardContent className="p-4 sm:p-6">
                            <div className="grid gap-6 lg:grid-cols-2">
                              <ResultImagePanel
                                image={activeExample.image}
                                label="Base Moondream 3 Preview"
                                pointOverlay={activeExample.before}
                              />
                              <ResultImagePanel
                                image={activeExample.image}
                                label="Fine-tuned Moondream 3 Preview"
                                pointOverlay={activeExample.after}
                              />
                            </div>
                          </CardContent>
                        </Card>

                        <ResultExampleControls
                          labels={tabLabels}
                          activeIndex={safeIndex}
                          onSelect={setActiveIndex}
                          legendKind="point"
                        />
                      </div>
                    </div>
                  </>
                );
              })()
            ) : variant === "answer" ? (
              <div className="space-y-6">
                {sharedAnswerPrompt ? (
                  <PromptCard prompt={sharedAnswerPrompt} />
                ) : null}

                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {answerExamples.map((example) => (
                    <AnswerExampleCard
                      key={example.id}
                      example={example}
                      showPrompt={!sharedAnswerPrompt}
                    />
                  ))}
                </div>
              </div>
            ) : (
              (() => {
                const activeExample = imageOverlayExamples[safeIndex]!;
                const delta =
                  typeof activeExample.before.miou === "number" &&
                  typeof activeExample.after.miou === "number"
                    ? activeExample.after.miou - activeExample.before.miou
                    : undefined;

                return (
                  <>
                    <div className="space-y-6">
                      <PromptCard prompt={finetune.codePrompt} />

                      <div className="space-y-4">
                        <Card
                          className="overflow-hidden border-border/60 bg-card/80 shadow-sm"
                          {...autoAdvanceHandlers}
                        >
                          <CardContent className="p-4 sm:p-6">
                            <div className="grid gap-6 lg:grid-cols-2">
                              <ResultImagePanel
                                image={activeExample.before.image}
                                label="Base Moondream 3 Preview"
                                metricLabel={
                                  typeof activeExample.before.miou === "number"
                                    ? "mIoU"
                                    : undefined
                                }
                                metricValue={activeExample.before.miou}
                              />
                              <ResultImagePanel
                                image={activeExample.after.image}
                                label="Fine-tuned Moondream 3 Preview"
                                metricLabel={
                                  typeof activeExample.after.miou === "number"
                                    ? "mIoU"
                                    : undefined
                                }
                                metricValue={activeExample.after.miou}
                                delta={delta}
                              />
                            </div>
                          </CardContent>
                        </Card>

                        <ResultExampleControls
                          labels={tabLabels}
                          activeIndex={safeIndex}
                          onSelect={setActiveIndex}
                        />
                      </div>
                    </div>
                  </>
                );
              })()
            )}
          </>
        )}
      </section>
    </PageContent>
  );
}
